import { useEffect, useMemo, useState } from 'react';
import { ModalProps } from '../../../../../../components/modal/modal';
import { NestedIdValue } from '../../../../../../features/multi-select/use-multi-select';
import { getValueFromOptions } from '../../../../../../helpers/mappers';
import { validate } from '../../../../../../helpers/validate';
import { useLang } from '../../../../../../hooks/use-lang';
import { PostScheduleRequest } from '../../../../../../store/api/scheduler-api';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import { useEmployeesStore } from '../../../../../../store/use-employee-store';
import { useTaskStore } from '../../../../../../store/use-task-store';
import { IdValue } from '../../../../../../types/props';

export const useTasksCreateTaskModal = (props: ModalProps) => {
  const { t, getLocale } = useLang();

  const { fetchEmployees, staff } = useEmployeesStore();
  const { place } = useBranchesStore();
  const { createTask, isLoadingFetch } = useTaskStore();

  const [task, setTask] = useState<Partial<PostScheduleRequest>>({
    is_regular: true,
    days_of_week: Array.from({ length: 7 }, () => false),
  });

  useEffect(() => {
    fetchEmployees({ page: 1, page_size: 1000, is_by_company: true, is_available: true });
  }, []);

  const handleCreate = async () => {
    try {
      const isValid = validate({
        name: { rule: () => !!task?.title && task.title.length > 0 },
        executor: { rule: () => !!task.staff_ids?.length },
      });

      if (!isValid || !task.staff_ids) return;

      await createTask({
        text: task.title!,
        staff_ids: task.staff_ids,
        place_id: task.place_id,
      });

      props.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const { availableStaff, availableStaffIdValue, branches, selectedBranch } = useMemo(() => {
    const branches =
      place?.map((item) => ({ id: String(item.id), value: getLocale(item.name) })) || [];
    const selectedBranch = branches.find((item) => item.id === String(task.place_id));
    const availableStaff =
      staff?.filter((item) =>
        item.places?.some((item) => item.id === Number(selectedBranch?.id)),
      ) || [];

    const availableStaffIdValue: IdValue[] =
      availableStaff?.map((item) => ({
        id: String(item.id),
        value: getLocale(item.name) + ' ' + getLocale(item.surname),
      })) || [];

    return { selectedBranch, availableStaff, availableStaffIdValue, branches };
  }, [place, task.place_id, staff]);

  const { executors, groupedStaffsByTags } = useMemo(() => {
    const groupedStaffsByTags: NestedIdValue[] = [];
    availableStaff?.forEach((item) => {
      const staff: IdValue = {
        id: String(item.id),
        value: getLocale(item.name) + ' ' + getLocale(item.surname),
      };
      item.tags?.forEach((tag) => {
        const tagId = String(tag.id);
        const tagIndex = groupedStaffsByTags.findIndex((resultItem) => resultItem.id === tagId);
        if (tagIndex === -1) {
          groupedStaffsByTags.push({
            id: tagId,
            value: tag.value,
            children: [staff],
          });
        } else {
          groupedStaffsByTags[tagIndex].children!.push(staff);
        }
      });
    });

    const executors = getValueFromOptions(
      availableStaffIdValue,
      task.staff_ids || [task.staff_id || -1],
    );

    return { branches, selectedBranch, executors, groupedStaffsByTags };
  }, [availableStaff, availableStaffIdValue, task.staff_ids]);

  return {
    t,
    task,
    handleCreate,
    setTask,
    groupedStaffsByTags,
    executors,
    branches,
    selectedBranch,
    isLoadingFetch,
  };
};
